import { useCallback } from 'react'
import { Tab } from './useTabManager'
import { getIconName, getIconComponent } from './tabSerialization'

/**
 * Serialized tab session stored in localStorage for a single Life Space.
 *
 * @property lifeSpace - Life Space ID this session belongs to
 * @property tabs - Array of serialized tab data (icon stored as string name)
 * @property activeTabId - ID of the last active tab in this session
 * @property timestamp - Unix timestamp (ms) when the session was saved; used for 1-week expiry
 */
export interface TabSession {
  lifeSpace: string
  tabs: Array<{
    id: string
    path: string
    title: string
    iconName?: string
    isPinned?: boolean
    isPreview?: boolean
  }>
  activeTabId?: string
  timestamp: number
}

/**
 * Hook for persisting and restoring tab sessions per Life Space.
 *
 * @description
 * Saves tab state synchronously to localStorage (key: `tab_session_{spaceId}`).
 * Sessions expire after 1 week. Icon components are serialized to string names
 * for JSON storage and restored via the Lucide icon registry.
 *
 * Note: No debounce — localStorage writes for small JSON payloads are < 1ms.
 * A shared debounce previously caused a bug where only the last space in the
 * save loop was persisted (lodash.debounce keeps only the last call's args).
 *
 * @returns An object containing:
 * - `saveSession` — Save tabs for a Life Space to localStorage
 * - `restoreSession` — Restore tabs from localStorage (returns `null` if expired or missing)
 * - `getSessionActiveTabId` — Get the last active tab ID from a saved session
 * - `clearSession` — Remove a single Life Space session
 * - `clearAllSessions` — Remove all tab sessions from localStorage
 * - `getIconName` — Resolve an icon component to its string name
 * - `getIconComponent` — Resolve an icon name string to its component
 *
 * @example
 * const { saveSession, restoreSession } = useTabPersistence()
 *
 * // Save current tabs
 * saveSession('personal', tabs, activeTabId)
 *
 * // Restore on mount
 * const restored = restoreSession('personal')
 */
export const useTabPersistence = () => {
  /**
   * Save session synchronously to localStorage.
   */
  const saveSession = useCallback((lifeSpace: string, tabs: Tab[], activeTabId?: string | null) => {
    try {
      const session: TabSession = {
        lifeSpace,
        tabs: tabs.map(tab => ({
          id: tab.id,
          path: tab.path,
          title: tab.title,
          iconName: getIconName(tab.icon),
          isPinned: tab.isPinned,
          isPreview: tab.isPreview
        })),
        activeTabId: activeTabId || undefined,
        timestamp: Date.now()
      }

      localStorage.setItem(`tab_session_${lifeSpace}`, JSON.stringify(session))
    } catch (error) {
      console.error('Failed to save tab session:', error)
    }
  }, [])

  /**
   * Restore tab session for a life space
   */
  const restoreSession = useCallback((lifeSpace: string): Tab[] | null => {
    try {
      const sessionStr = localStorage.getItem(`tab_session_${lifeSpace}`)
      if (!sessionStr) return null

      const session = JSON.parse(sessionStr) as TabSession

      // Check if session is expired (1 week)
      const ONE_WEEK = 7 * 24 * 60 * 60 * 1000
      if (Date.now() - session.timestamp > ONE_WEEK) {
        localStorage.removeItem(`tab_session_${lifeSpace}`)
        return null
      }

      // Restore tabs with proper icon components
      const restoredTabs: Tab[] = session.tabs.map(tab => ({
        ...tab,
        icon: getIconComponent(tab.iconName),
        lifeSpace,
        isDirty: false, // Reset dirty state on restore
        isPreview: tab.isPreview || false
      }))

      return restoredTabs
    } catch (error) {
      console.error('Failed to restore tab session:', error)
      return null
    }
  }, [])

  /**
   * Get active tab ID from session
   */
  const getSessionActiveTabId = useCallback((lifeSpace: string): string | null => {
    try {
      const sessionStr = localStorage.getItem(`tab_session_${lifeSpace}`)
      if (!sessionStr) return null

      const session = JSON.parse(sessionStr) as TabSession
      return session.activeTabId || null
    } catch {
      return null
    }
  }, [])

  /**
   * Clear session for a life space
   */
  const clearSession = useCallback((lifeSpace: string) => {
    localStorage.removeItem(`tab_session_${lifeSpace}`)
  }, [])

  /**
   * Clear all sessions
   */
  const clearAllSessions = useCallback(() => {
    const keys = Object.keys(localStorage)
    keys.forEach(key => {
      if (key.startsWith('tab_session_')) {
        localStorage.removeItem(key)
      }
    })
  }, [])

  return {
    saveSession,
    restoreSession,
    getSessionActiveTabId,
    clearSession,
    clearAllSessions,
    getIconName,
    getIconComponent
  }
}
